import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import ClientLayout from '../../components/layouts/ClientLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { 
  ClipboardList, CheckCircle2, Clock, Loader2, Calendar, RefreshCw,
  MessageSquare, Send, FileText, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TASK_CATEGORIES = [
  { value: 'venue', label: 'Venue & Location' },
  { value: 'catering', label: 'Food & Catering' },
  { value: 'equipment', label: 'Sound & Equipment' },
  { value: 'decoration', label: 'Decoration & Setup' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'general', label: 'General' }
];

const ClientTasks = () => {
  const { user, permissions } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedTask, setSelectedTask] = useState(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [newReport, setNewReport] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Check if user has logistics permission
  const canManageTasks = permissions?.includes('manage_logistics');

  useEffect(() => {
    if (user?.event_id) {
      fetchTasks();
    }
  }, [user]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/event/tasks?event_id=${user.event_id}`);
      setTasks(response.data);
    } catch (error) {
      console.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (taskId, currentStatus) => {
    if (!canManageTasks) {
      toast.error('You need Logistics committee access to manage tasks');
      return;
    }
    
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    try {
      await axios.put(`${API_URL}/api/event/tasks/${taskId}/status?status=${newStatus}`);
      fetchTasks();
      toast.success(newStatus === 'completed' ? 'Task completed!' : 'Task reopened');
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleSubmitReport = async () => {
    if (!newReport.trim()) {
      toast.error('Please enter a report');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/event/tasks/${selectedTask.id}/reports`, {
        content: newReport
      });
      toast.success('Report submitted successfully');
      setNewReport('');
      setReportDialogOpen(false);
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  const openReportDialog = (task) => {
    setSelectedTask(task);
    setReportDialogOpen(true);
  };

  const filteredTasks = tasks.filter(task => {
    if (filter === 'all') return true;
    if (filter === 'pending') return task.status === 'pending';
    if (filter === 'completed') return task.status === 'completed';
    if (filter === 'mine') return task.assigned_to === user?.id;
    return task.category === filter;
  });

  const myTasks = tasks.filter(t => t.assigned_to === user?.id);
  const pendingCount = tasks.filter(t => t.status === 'pending').length;
  const completedCount = tasks.filter(t => t.status === 'completed').length;

  const getCategoryLabel = (value) => {
    return TASK_CATEGORIES.find(c => c.value === value)?.label || value;
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-PH', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-maroon" />
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="space-y-6 animate-fade-in" data-testid="client-tasks-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-maroon flex items-center gap-3">
              <ClipboardList className="h-8 w-8" />
              Event Tasks
            </h1>
            <p className="text-gray-600 mt-1">
              {canManageTasks ? 'Manage logistics and preparation tasks' : 'View event preparation progress'}
            </p>
          </div>
          
          <Button 
            variant="outline" 
            onClick={fetchTasks}
            className="gap-2"
            data-testid="refresh-tasks-btn"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>

        {/* Committee Badge */}
        {canManageTasks && (
          <div className="bg-gold/10 border border-gold/30 rounded-xl p-4 flex items-center gap-3">
            <Badge className="bg-gold text-gray-900">Logistics Committee</Badge>
            <span className="text-sm text-gray-700">You have permission to manage tasks</span>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ClipboardList className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{tasks.length}</p>
                <p className="text-sm text-gray-500">Total Tasks</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{pendingCount}</p>
                <p className="text-sm text-gray-500">Pending</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{completedCount}</p>
                <p className="text-sm text-gray-500">Completed</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-maroon text-white">
            <CardContent className="p-4 flex items-center gap-3">
              <div>
                <p className="text-2xl font-bold">{myTasks.length}</p>
                <p className="text-sm text-white/80">Assigned to Me</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('all')}
            className={filter === 'all' ? 'bg-maroon hover:bg-maroon-hover' : ''}
          >
            All
          </Button>
          <Button
            variant={filter === 'mine' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('mine')}
            className={filter === 'mine' ? 'bg-maroon hover:bg-maroon-hover' : ''}
          >
            My Tasks ({myTasks.length})
          </Button>
          <Button
            variant={filter === 'pending' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('pending')}
            className={filter === 'pending' ? 'bg-amber-500 hover:bg-amber-600' : ''}
          >
            Pending
          </Button>
          <Button
            variant={filter === 'completed' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('completed')}
            className={filter === 'completed' ? 'bg-green-500 hover:bg-green-600' : ''}
          >
            Completed
          </Button>
        </div>

        {/* Task List */}
        {filteredTasks.length === 0 ? (
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="p-12 text-center">
              <ClipboardList className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-600">No tasks found</h3>
              <p className="text-gray-400 mt-1">
                {filter === 'mine' ? 'No tasks assigned to you yet' : 'No tasks available'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredTasks.map((task) => {
              const isAssignedToMe = task.assigned_to === user?.id;
              const hasReports = task.reports && task.reports.length > 0;
              
              return (
                <Card 
                  key={task.id}
                  className={`border-0 shadow-sm transition-all ${
                    task.status === 'completed' ? 'bg-green-50' : 
                    isAssignedToMe ? 'bg-gold/10 border-l-4 border-l-gold' : 'bg-white hover:shadow-md'
                  }`}
                  data-testid={`task-card-${task.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <Checkbox
                        checked={task.status === 'completed'}
                        onCheckedChange={() => handleToggleStatus(task.id, task.status)}
                        disabled={!canManageTasks}
                        className="mt-1 h-5 w-5"
                        data-testid={`task-checkbox-${task.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <Badge variant="outline" className="text-xs">
                            {getCategoryLabel(task.category)}
                          </Badge>
                          {isAssignedToMe && (
                            <Badge className="bg-gold text-gray-900 text-xs">
                              Assigned to You
                            </Badge>
                          )}
                          {task.status === 'completed' && (
                            <Badge className="bg-green-100 text-green-700 text-xs">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Done
                            </Badge>
                          )}
                          {hasReports && (
                            <Badge variant="outline" className="text-xs text-blue-600 border-blue-200">
                              <MessageSquare className="h-3 w-3 mr-1" />
                              {task.reports.length} report{task.reports.length > 1 ? 's' : ''}
                            </Badge>
                          )}
                        </div>
                        <h3 className={`font-semibold ${task.status === 'completed' ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                          {task.title}
                        </h3>
                        {task.description && (
                          <p className="text-sm text-gray-500 mt-1">{task.description}</p>
                        )}
                        {task.due_date && (
                          <div className="flex items-center gap-1 mt-2 text-xs text-gray-400">
                            <Calendar className="h-3 w-3" />
                            Due: {new Date(task.due_date).toLocaleDateString()}
                          </div>
                        )}

                        {/* Reports Section */}
                        {hasReports && (
                          <div className="mt-4 border-t pt-4 space-y-3">
                            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                              <MessageSquare className="h-4 w-4" />
                              Task Reports
                            </h4>
                            {task.reports.map((report, idx) => (
                              <div 
                                key={report.id || idx}
                                className="bg-white rounded-lg p-3 border border-gray-100"
                              >
                                <div className="flex items-center gap-2 mb-2">
                                  <Avatar className="h-6 w-6">
                                    <AvatarFallback className="bg-maroon text-white text-xs">
                                      {getInitials(report.user_name)}
                                    </AvatarFallback>
                                  </Avatar>
                                  <span className="font-medium text-sm text-gray-900">
                                    {report.user_name}
                                  </span>
                                  <span className="text-xs text-gray-400">
                                    {formatDate(report.created_at)}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-700 pl-8">{report.content}</p>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Add Report Button - Only for assigned user */}
                        {isAssignedToMe && task.status !== 'completed' && (
                          <div className="mt-4">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openReportDialog(task)}
                              className="gap-2 text-blue-600 border-blue-200 hover:bg-blue-50"
                              data-testid={`add-report-btn-${task.id}`}
                            >
                              <MessageSquare className="h-4 w-4" />
                              Add Report / Update
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Note about Documentation Committee */}
        <Card className="border-0 shadow-sm bg-blue-50 border-l-4 border-l-blue-500">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-blue-900">Note about Documents</p>
              <p className="text-sm text-blue-700 mt-1">
                Submit any documents or files related to your task reports to the <strong>Documentation Committee</strong> for uploading. 
                They will organize and upload all files to the document library for everyone to access.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Report Dialog */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-maroon flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Submit Task Report
            </DialogTitle>
            <DialogDescription>
              Add an update or progress report for: <strong>{selectedTask?.title}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={newReport}
              onChange={(e) => setNewReport(e.target.value)}
              placeholder="Share your progress, updates, or any issues you've encountered..."
              rows={4}
              data-testid="report-content-input"
            />
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
              <FileText className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">
                <strong>Need to attach files?</strong> Submit them separately to the Documentation Committee for uploading to the document library.
              </p>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setReportDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmitReport}
                disabled={submitting || !newReport.trim()}
                className="bg-maroon hover:bg-maroon-hover text-white gap-2"
                data-testid="submit-report-btn"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Submit Report
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </ClientLayout>
  );
};

export default ClientTasks;
